import axios from 'axios';
import endpoints from 'constants/endpoints';
import { safeAWSCallPost } from 'helpers/firebaseSetup';
import { getCompany } from 'helpers/getCompanyVariables';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getLastUpdatedateBOB } from 'state/actions/commissions';

const company = getCompany();

export function getInitialValues(riskManagement) {
  return {
    searchTerm: '', // This field searches across customer name, policy number, phone number, and email address
    memberStatus: riskManagement ? ['At Risk'] : [],
    fallenOff: company === 'oneill' ? ['On BOB'] : [],
    agents: [],
    carriers: [],
    agentStatus: ['Active', 'Inactive'],
    netPremiumFilter: null,
    netPremiumValue: null,
    effectiveDate: {
      startDate: null,
      endDate: null
    }
  };
}

const useBobData = ({
  setLocalLoader,
  setFilterCount,
  setShowFilters,
  setActivePanel,
  riskManagement
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(50);
  const [dataForTable, setDataForTable] = useState([]);
  const [bobRecordsCount, setBobRecordsCount] = useState(0);
  const [formData, setformData] = useState(getInitialValues(riskManagement));
  const [lastUpdateDate, setLastUpdateDate] = useState(null);
  const [triggerFetch, setTriggerFetch] = useState(1);
  const [selectedFilterData, setSelectedFilterData] = useState(
    getInitialValues(riskManagement)
  );
  const [retentionRateData, setRetentionRateData] = useState([]);
  const [retentionRateYear, setRetentionRateYear] = useState('current');

  const { satementsCarriersArray } = useSelector(state => state.appointments);
  const cancelTokenSource = axios.CancelToken.source();
  const loadingCountRef = useRef(0);

  const carriersOptions = React.useMemo(() => {
    const carriers = new Set([
      'Anthem',
      'Ambetter',
      'AmeriHealth Caritas',
      'Aetna',
      'BCBS IL',
      'BCBS OK',
      'BCBS TX',
      'Cigna',
      'Oscar',
      'United Health Care',
      'Molina'
    ]);
    return satementsCarriersArray
      .filter(carrier => carrier.Callidus_Carrier_Names !== undefined)
      .reduce((acc, option) => {
        const callidusNames = [];
        for (const name of option.Callidus_Carrier_Names) {
          if (carriers.has(name)) {
            callidusNames.push({
              type: option.type,
              name: name,
              id: option.AgencyBloc_CarrierID,
              Callidus_Carrier_Names: name
            });
          }
        }
        return [...acc, ...callidusNames];
      }, [])
      .filter(carrier => carrier.type);
  }, [satementsCarriersArray]);

  const getRetentionRateData = useCallback(
    (agentIds, yearFilter = 'current') => {
      if (!riskManagement) return;
      if (!agentIds || agentIds.length === 0) {
        setRetentionRateData([]);
        return;
      }
      loadingCountRef.current += 1;
      if (loadingCountRef.current === 1) {
        setLocalLoader(true);
      }
      safeAWSCallPost(
        endpoints.aws.getRetentionRate,
        {
          agents: agentIds,
          yearFilter: yearFilter
        },
        false,
        cancelTokenSource.token
      )
        .then(response => {
          setRetentionRateData(response.data || []);
          loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
          if (loadingCountRef.current === 0) {
            setLocalLoader(false);
          }
        })
        .catch(error => {
          console.error('Retention Rate - Error:', error);
          setRetentionRateData([]);
          loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
          if (loadingCountRef.current === 0) {
            setLocalLoader(false);
          }
        });
    },
    [setLocalLoader, riskManagement]
  );

  const getBobData = useCallback(
    (page, filters, skipCountPages) => {
      setActivePanel({});
      loadingCountRef.current += 1;
      if (loadingCountRef.current === 1) {
        setLocalLoader(true);
      }
      safeAWSCallPost(
        endpoints.aws.getBobTabData,
        {
          page: page - 1,
          records_per_page: currentPageSize,
          filters: JSON.stringify({
            ...filters,
            agents: [],
            skipCountPages
          }),
          agents: filters.agents.map(agent => agent.id)
        },
        false,
        cancelTokenSource.token
      )
        .then(response => {
          setDataForTable(response.data.records);
          if (!skipCountPages) {
            setBobRecordsCount(response.data.totalRows);
          }
          loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
          if (loadingCountRef.current === 0) {
            setLocalLoader(false);
          }
        })
        .catch(() => {
          setDataForTable([]);
          setBobRecordsCount(0);
          loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
          if (loadingCountRef.current === 0) {
            setLocalLoader(false);
          }
        });

      const tbodyElement = document.getElementById('customers-bob-table-body');
      if (tbodyElement) {
        tbodyElement.scrollTop = 0;
      }
    },
    [currentPageSize, setLocalLoader, setActivePanel]
  );

  // Effect for BOB data - triggers on pagination and filter changes
  useEffect(() => {
    if (formData.agents.length === 0) return;
    getBobData(currentPage, formData, true);
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, [triggerFetch, getBobData]);

  // Effect for retention rates - only triggers on filter changes or year changes
  useEffect(() => {
    if (!riskManagement || formData.agents.length === 0) return;
    getRetentionRateData(
      formData.agents.map(agent => agent.id),
      retentionRateYear
    );
  }, [riskManagement, retentionRateYear, getRetentionRateData]);

  useEffect(() => {
    getLastUpdatedateBOB().then(res => {
      setLastUpdateDate(res);
    });
  }, []);

  const handleNetPremiumFilterChange = useCallback(
    filterValue => {
      setformData(prevState => {
        const wasActive =
          prevState.netPremiumFilter !== null &&
          prevState.netPremiumValue !== null;
        const newFilter = filterValue === '' ? null : filterValue;
        const willBeActive =
          newFilter !== null && prevState.netPremiumValue !== null;

        // Update filter count
        if (!wasActive && willBeActive) {
          setFilterCount(count => count + 1);
        } else if (wasActive && !willBeActive) {
          setFilterCount(count => count - 1);
        }

        return {
          ...prevState,
          netPremiumFilter: newFilter,
          // Clear value when filter type is cleared
          netPremiumValue: newFilter === null ? null : prevState.netPremiumValue
        };
      });
    },
    [setFilterCount]
  );

  const handleNetPremiumValueChange = useCallback(
    value => {
      setformData(prevState => {
        const wasActive =
          prevState.netPremiumFilter !== null &&
          prevState.netPremiumValue !== null;
        let newValue;
        if (value === '') {
          newValue = null;
        } else if (typeof value === 'number') {
          newValue = value;
        } else {
          newValue = Number.parseFloat(value);
        }
        const willBeActive =
          prevState.netPremiumFilter !== null && newValue !== null;

        // Update filter count
        if (!wasActive && willBeActive) {
          setFilterCount(count => count + 1);
        } else if (wasActive && !willBeActive) {
          setFilterCount(count => count - 1);
        }

        return {
          ...prevState,
          netPremiumValue: newValue
        };
      });
    },
    [setFilterCount]
  );

  const handleEffectiveDateChange = useCallback(
    (dateType, dateValue) => {
      setformData(prevState => {
        const wasActive =
          prevState.effectiveDate.startDate !== null &&
          prevState.effectiveDate.endDate !== null;
        const newDate =
          dateValue === null || dateValue === '' ? null : dateValue;

        const updatedEffectiveDate = {
          ...prevState.effectiveDate,
          [dateType]: newDate
        };

        const willBeActive =
          updatedEffectiveDate.startDate !== null &&
          updatedEffectiveDate.endDate !== null;

        // Update filter count
        if (!wasActive && willBeActive) {
          setFilterCount(count => count + 1);
        } else if (wasActive && !willBeActive) {
          setFilterCount(count => count - 1);
        }

        return {
          ...prevState,
          effectiveDate: updatedEffectiveDate
        };
      });
    },
    [setFilterCount]
  );

  const handleChangeSelectValue = useCallback(
    (_values, property) => {
      let values = [];
      if (typeof _values === 'string') {
        values = formData[property][0] === _values ? [] : [_values];
      } else {
        values = _values;
      }
      if (
        formData[property].length === 0 &&
        values.length > 0 &&
        property !== 'agents'
      ) {
        setFilterCount(count => count + 1);
      } else if (
        formData[property].length > 0 &&
        values.length === 0 &&
        property !== 'agents'
      ) {
        setFilterCount(count => count - 1);
      }
      setformData(prevState => ({
        ...prevState,
        [property]: values
      }));
    },
    [formData, setFilterCount]
  );

  const clearFilters = useCallback(() => {
    const clearedFormData = {
      ...getInitialValues(riskManagement),
      agents: [...formData.agents]
    };
    // If agentStatus defaults to "Active", setFilterCount to 1; else, 0. Adjust if needed.
    setFilterCount(1);
    setformData(clearedFormData);
    setSelectedFilterData(clearedFormData);
  }, [
    setFilterCount,
    setformData,
    setSelectedFilterData,
    formData.agents,
    riskManagement
  ]);

  const applyFilters = useCallback(
    (reset = false) => {
      if (formData.agents.length === 0) return;
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      if (reset) {
        const resetFormData = {
          ...getInitialValues(riskManagement),
          agents: [...formData.agents]
        };
        getBobData(currentPage, resetFormData, false);
        if (riskManagement) {
          getRetentionRateData(
            resetFormData.agents.map(agent => agent.id),
            retentionRateYear
          );
        }
      } else {
        getBobData(currentPage, formData, false);
        if (riskManagement) {
          getRetentionRateData(
            formData.agents.map(agent => agent.id),
            retentionRateYear
          );
        }
      }
      setShowFilters(false);
    },
    [currentPage, getBobData, formData, riskManagement, getRetentionRateData]
  );

  return {
    data_for_table: dataForTable,
    currentPage,
    setCurrentPage,
    setCurrentPageSize,
    bob_records_count: bobRecordsCount,
    formData,
    setformData,
    clearFilters,
    handleChangeSelectValue,
    handleNetPremiumFilterChange,
    handleNetPremiumValueChange,
    handleEffectiveDateChange,
    applyFilters,
    setTriggerFetch,
    carriersOptions,
    lastUpdateDate,
    selectedFilterData,
    setSelectedFilterData,
    retentionRateData,
    retentionRateYear,
    setRetentionRateYear
  };
};

useBobData.propTypes = {
  loadingApptsData: PropTypes.func,
  setLocalLoader: PropTypes.func,
  setFilterCount: PropTypes.func,
  setShowFilters: PropTypes.func,
  setActivePanel: PropTypes.func
};

export default useBobData;
